import { HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { OAuth2Tokens } from '@forgerock/javascript-sdk';
import { EMPTY } from 'rxjs';

import { env } from '@amfam/environment';
import { Partners } from '@amfam/shared/utility/brand/lib/services/brand.enum';
import { LOGOUT_ORIGINS } from '@amfam/shared/utility/shared-services/lib/core/logout-origin';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { ComponentViewService } from '@amfam/ui-kit/lib/shared-components/component-view.service';

import { SessionActivityService } from '../../login/session-activity/session-activity.service';
import { AfeSessionService } from '../auth/afe-session.service';

const tokenIgnoreList = ['/assets/featureflags/featureflags'];

/** Pass untouched request through to the next request handler. */
export const globalHeadersInterceptor: HttpInterceptorFn = (request, next) => {
  const sessionActivityService = inject(SessionActivityService);
  const afeSessionService = inject(AfeSessionService);
  const componentViewService = inject(ComponentViewService);
  const utilService = inject(UtilService);

  // Check if this endpoint requires ForgeRock authentication
  const requiresAuth = requiresAuthentication(request.url);

  // Get tokens synchronously from localStorage (try both possible keys)
  const tokens = getForgerockTokens(request.url, utilService);

  // If auth is required but no valid tokens, block the request
  if (requiresAuth && !tokens && !componentViewService.inWebview) {
    utilService.error('[Auth] Interceptor session expired');
    sessionActivityService.logout('/session-expired', LOGOUT_ORIGINS.INTERCEPTOR_NO_TOKENS);
    return EMPTY;
  }

  // Proceed with request
  const modified = request.clone({
    headers:
      request.url.includes('apiext') ||
      request.url.includes('retention-mock-server-sbx') ||
      request.url.includes('ssg') ||
      request.url.includes('pciAuthorizationService') ||
      (request.url.includes('party/v3') && env.myAccountBuildGroupId === Partners.LEMONADE) ||
      request.url.includes('finacctservice')
        ? addL7Headers(request.headers, request.method, tokens)
        : addApigeeHeaders(request.headers, tokens, afeSessionService),
    withCredentials: true
  });

  return next(modified);
};

function addApigeeHeaders(
  headers: HttpHeaders,
  tokens: OAuth2Tokens | null,
  afeSessionService: AfeSessionService
): HttpHeaders {
  if (!headers.has('afe-source-id')) {
    headers = headers.set('afe-source-id', 'MyAccount');
  }
  if (!headers.has('afe-api-key')) {
    headers = headers.set('afe-api-key', env.afeApiKey);
  }
  if (!headers.has('afe-session-id')) {
    const sessionId = afeSessionService.getSessionId();
    if (sessionId) {
      headers = headers.set('afe-session-id', sessionId);
    }
  }

  if (!headers.has('afe-trace-id')) {
    headers = headers.set('afe-trace-id', crypto.randomUUID());
  }

  headers = commonheaders(headers, tokens);
  return headers;
}

function addL7Headers(
  headers: HttpHeaders,
  method: string,
  tokens: OAuth2Tokens | null
): HttpHeaders {
  if (!headers.has('accept')) {
    headers = headers.set('accept', 'application/json');
  }
  if (!headers.has('content-type')) {
    headers = headers.set('content-type', 'application/json');
  }
  if (method === 'GET') {
    if (!headers.has('cache-control')) {
      headers = headers.set('cache-control', 'no-cache');
    }
    if (!headers.has('pragma')) {
      headers = headers.set('pragma', 'no-cache');
    }
  }
  headers = commonheaders(headers, tokens);
  return headers;
}

function commonheaders(headers: HttpHeaders, tokens: OAuth2Tokens | null): HttpHeaders {
  if (!headers.has('afi-appname')) {
    headers = headers.set('afi-appname', 'MyAccountWebApplication');
  }
  if (!headers.has('afi-api-key')) {
    headers = headers.set('afi-api-key', env.afiApiKey);
  }
  if (!headers.has('authorization') && tokens?.accessToken) {
    headers = headers.set('authorization', 'Bearer ' + tokens.accessToken);
  }
  return headers;
}

/**
 * Check if the URL requires ForgeRock tokens based on predefined patterns
 * This is a simple string check but can be enhanced to use regex or a more robust pattern matching if needed
 */
function requiresAuthentication(url: string): boolean {
  return (
    (!url.includes('/exp-brand/') &&
      !url.includes('/exp-guest-pay/') &&
      url.includes('af1platform.com')) ||
    url.includes('pciAuthorizationService') ||
    url.includes('finacctservice')
  );
}

/**
 * Get ForgeRock tokens synchronously from localStorage
 * Tries both possible keys to avoid environment-specific logic
 */
function getForgerockTokens(url: string, utilService: UtilService): OAuth2Tokens | null {
  try {
    const forgerockKey = `FR-SDK-${env.forgerock.webOauthClient}`;

    const tokensStr = localStorage.getItem(forgerockKey);
    if (tokensStr) {
      const tokens = JSON.parse(tokensStr) as OAuth2Tokens;

      // Validate token exists
      if (!tokens?.accessToken) {
        utilService.warn(
          `[Auth] Interceptor Invalid forgerock token -- URL: ${utilService.maskSensitiveParams(url)}`
        );
        return null;
      }

      // Check if token is expired
      if (tokens?.tokenExpiry) {
        const expiryDate = new Date(tokens.tokenExpiry);
        if (new Date() >= expiryDate) {
          utilService.warn(
            `[Auth] Interceptor ForgeRock token is expired -- ${expiryDate?.toTimeString()} -- URL: ${utilService.maskSensitiveParams(url)}`
          );
          return null;
        }
      }

      // Found valid tokens
      return tokens;
    }

    if (!tokenIgnoreList.some(str => url.includes(str))) {
      utilService.error(
        `[Auth] Interceptor Unable to fetch tokens from localstorage for -- ${forgerockKey} -- URL: ${utilService.maskSensitiveParams(url)}`
      );
    }

    // No valid tokens found in either key
    return null;
  } catch (error) {
    utilService.error(
      `[Auth] Interceptor Error reading ForgeRock tokens from localStorage -- URL: ${utilService.maskSensitiveParams(url)}`,
      {
        error: error
      }
    );

    return null;
  }
}
